/**
 * RoofCrewSection — Pending crew payments (Risol Construction default) on the
 * Commissions tab of the Finance Board.
 *
 * Each card shows:
 *   - Crew vendor (default Risol; dropdown for "+ Add other vendor" later)
 *   - Total squares (locked, from PM Board post-build OR deals.total_squares)
 *   - Rate per sq ($90 default, auto-bumped to $100 at ≥60sq per Greg's rule)
 *   - Flat fee ($350 default, editable)
 *   - Additional approved work — amount + DESCRIPTION (required for non-zero
 *     amount; shown on Risol's payment email from Books, under the job)
 *   - Total = (squares × rate) + flat + additional
 *   - "Approve crew payment" button (Chad) → flips deal.roof_crew_payment_approved=true
 *
 * On approve (Oct 8, 2026): the `roof_crew_payment_approve` RPC upserts the
 * roof_crew_payments row in status='approved', sets the
 * deal.roof_crew_payment_approved gate flag (this unblocks RM/TL/ASM commission
 * approval — see CommissionBoard), writes the audit row and makes ONE Books
 * bill per crew (vendor) for the batch; Shannon pays it in Books.
 *
 * Spec: memory/features/finance-board-redesign.md (Ship 5, §3, §4b, §10b #6, §10c #1)
 */

import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import type { Deal, TeamRole } from "../../lib/types";
import { fmtUSD } from "../../lib/analytics";
import DealLink from "../DealLink";
import { formatSupabaseError } from "../../lib/formatSupabaseError";

type DealForCrew = Pick<Deal, "id" | "homeowner_name" | "claim_number" | "property_street" | "build_completed_date" | "total_squares" | "pm_workbook" | "roof_crew_payment_approved">;

interface Vendor {
  id: string;
  name: string;
  kind: string;
  default_rate_per_sq: number | null;
  default_flat_fee: number;
  primary_email: string | null;
  is_active: boolean;
}

interface RoofCrewPayment {
  id: string;
  deal_id: string;
  vendor_id: string;
  squares: number;
  rate_per_sq: number;
  flat_fee: number;
  additional_work_amount: number;
  additional_work_description: string | null;
  total: number;
  status: "pending" | "approved" | "paid" | "voided";
  created_at: string;
  /** Oct 8, 2026 — the Books bill that carries this payment (set at approval). */
  books_bill_id?: string | null;
}

const RATE_THRESHOLD_SQ = 60;  // ≥60sq auto-bumps to $100/sq per Greg's rule
const RATE_LOW = 90;
const RATE_HIGH = 100;
const DEFAULT_FLAT = 350;

export default function RoofCrewSection({
  deals,
  role,
  isSuperUser,
  onApproved,
}: {
  deals: DealForCrew[];
  role: TeamRole | undefined;
  isSuperUser: boolean;
  onApproved?: () => void;
}) {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [existingPayments, setExistingPayments] = useState<RoofCrewPayment[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [acting, setActing] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  // Per-deal edit state — vendor + flat + additional ($) + description.
  // Squares + rate are computed (squares from PM Board, rate auto-bumps).
  const [edits, setEdits] = useState<Record<string, { vendorId: string; flatFee: number; addtlAmount: number; addtlDesc: string }>>({});

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const [vRes, pRes] = await Promise.all([
        supabase.from("vendors").select("*").eq("kind", "roof_crew").eq("is_active", true).order("name"),
        supabase.from("roof_crew_payments").select("*").in("deal_id", deals.map(d => d.id)),
      ]);
      if (cancelled) return;
      if (vRes.error || pRes.error) {
        setError(vRes.error?.message ?? pRes.error?.message ?? "Failed loading crew payment data");
        return;
      }
      setVendors((vRes.data ?? []) as Vendor[]);
      setExistingPayments((pRes.data ?? []) as RoofCrewPayment[]);

      // Initialize per-deal edits.
      // Priority: existing pending payment row > Robert's PM Board entry > defaults.
      // Robert types additional-work amount + description on the PM Board post-build
      // section as he sees the crew do extra work; those values pre-fill Chad's
      // approval card here so Chad's not re-entering from a text message.
      const risol = (vRes.data ?? []).find((v: Vendor) => v.name.toLowerCase().includes("risol"));
      const init: Record<string, { vendorId: string; flatFee: number; addtlAmount: number; addtlDesc: string }> = {};
      for (const d of deals) {
        const existing = (pRes.data ?? []).find((p: RoofCrewPayment) => p.deal_id === d.id && p.status === "pending");
        if (existing) {
          init[d.id] = {
            vendorId: existing.vendor_id,
            flatFee: Number(existing.flat_fee),
            addtlAmount: Number(existing.additional_work_amount),
            addtlDesc: existing.additional_work_description ?? "",
          };
        } else {
          // Pull Robert's pre-fill from pm_workbook.post_build if present.
          const pmPostBuild = (d.pm_workbook as any)?.post_build ?? {};
          const pmAddtlAmount = Number(pmPostBuild.crew_additional_work_amount) || 0;
          const pmAddtlDesc: string = typeof pmPostBuild.crew_additional_work_description === "string"
            ? pmPostBuild.crew_additional_work_description
            : "";
          init[d.id] = {
            vendorId: risol?.id ?? "",
            flatFee: DEFAULT_FLAT,
            addtlAmount: pmAddtlAmount,
            addtlDesc: pmAddtlDesc,
          };
        }
      }
      setEdits(init);
      setLoaded(true);
    }
    load();
    return () => { cancelled = true; };
  }, [deals]);

  const canApprove = useMemo(() => {
    return isSuperUser || role === "admin" || role === "finance" || role === "sales_director";
  }, [role, isSuperUser]);

  function squaresFor(d: DealForCrew): number {
    // Mirror PM Board's Post-Build Tracking "Total Square Count" tile exactly:
    //   bundlesOrdered    = qty_duration + qty_proedge + qty_starter
    //   totalBundleCount  = bundlesOrdered + additional_bundles - total_leftover
    //   totalSquareCount  = totalBundleCount / 3
    // We derive it live from the workbook because PM Board renders that
    // number in JSX without persisting it back to JSONB, so the stored
    // post_build.total_square_count is 0 on every deal until the PMBoardTab
    // sync useEffect catches up (see ship deployed alongside this fix).
    // Order: live derivation → stored total_square_count → deal.total_squares.
    const wb = d.pm_workbook as any;
    if (wb) {
      const qtyDuration = Number(wb.qty_duration) || 0;
      const qtyProedge  = Number(wb.qty_proedge)  || 0;
      const qtyStarter  = Number(wb.qty_starter)  || 0;
      const additional  = Number(wb.post_build?.additional_bundles) || 0;
      const leftover    = Number(wb.post_build?.total_leftover)     || 0;
      const bundlesOrdered   = qtyDuration + qtyProedge + qtyStarter;
      const totalBundleCount = bundlesOrdered + additional - leftover;
      const derived = totalBundleCount / 3;
      if (derived > 0) {
        // Round to nearest whole square — Risol bills in whole squares and
        // PM Board's display is `.toFixed(1)`, so a 0.5 here is meaningful.
        // We keep one decimal of precision so a 19.7sq job doesn't round
        // down to 19sq and shortchange the crew.
        return Math.round(derived * 10) / 10;
      }
      const stored = Number(wb.post_build?.total_square_count) || 0;
      if (stored > 0) return stored;
    }
    return Number(d.total_squares) || 0;
  }

  function rateFor(squares: number): number {
    return squares >= RATE_THRESHOLD_SQ ? RATE_HIGH : RATE_LOW;
  }

  function totalFor(d: DealForCrew): number {
    const e = edits[d.id];
    if (!e) return 0;
    const sq = squaresFor(d);
    const rate = rateFor(sq);
    return sq * rate + (Number(e.flatFee) || 0) + (Number(e.addtlAmount) || 0);
  }

  async function approveCrew(d: DealForCrew) {
    setError(null);
    const e = edits[d.id];
    if (!e) return;
    if (!e.vendorId) {
      setError("Pick a crew vendor first.");
      return;
    }
    if (Number(e.addtlAmount) > 0 && (e.addtlDesc || "").trim().length < 5) {
      setError("Description required when there's an additional work amount (≥5 chars).");
      return;
    }

    setActing(new Set([...acting, d.id]));
    try {
      const sq = squaresFor(d);
      const rate = rateFor(sq);

      // Oct 8, 2026 — one RPC does what three writes did: upsert the row as
      // 'approved' (the pending row if there is one), flip the deal's gate
      // flag, write the audit row and make the crew's Books bill. The actor
      // is resolved server-side from the sign-in (team_roster by email).
      const existing = existingPayments.find((p) => p.deal_id === d.id && p.status === "pending");
      const { error: rpcErr } = await supabase.rpc("roof_crew_payment_approve", {
        p_rows: [{
          id: existing?.id ?? null,
          deal_id: d.id,
          vendor_id: e.vendorId,
          squares: sq,
          rate_per_sq: rate,
          flat_fee: Number(e.flatFee) || 0,
          additional_work_amount: Number(e.addtlAmount) || 0,
          additional_work_description: e.addtlDesc || null,
        }],
      });
      if (rpcErr) throw rpcErr;

      onApproved?.();
    } catch (err) {
      setError(formatSupabaseError(err));
    } finally {
      setActing((prev) => { const n = new Set(prev); n.delete(d.id); return n; });
    }
  }

  if (!loaded) return <div className="text-sm text-slate-500 p-4">Loading roof crew approvals…</div>;

  // Filter to deals that need crew approval (status pending or absent)
  const needsApproval = deals.filter((d) => {
    if (d.roof_crew_payment_approved) return false;
    return true;
  });

  if (needsApproval.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow p-6 text-sm text-slate-500 text-center">
        No roof crew approvals pending.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {error && (
        <div className="bg-red-50 text-red-700 text-xs p-2 rounded">⚠ {error}</div>
      )}
      {needsApproval.map((d) => {
        const e = edits[d.id];
        if (!e) return null;
        const sq = squaresFor(d);
        const rate = rateFor(sq);
        const total = totalFor(d);
        const sqMissing = sq === 0;
        const isAutoBump = sq >= RATE_THRESHOLD_SQ;
        const isActing = acting.has(d.id);

        return (
          <div key={d.id} className="bg-white rounded-xl shadow p-4">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm truncate">
                  <DealLink id={d.id}>{d.homeowner_name ?? "(no name)"}</DealLink>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {d.property_street ?? "—"}{d.claim_number ? ` · #${d.claim_number}` : ""}
                  {d.build_completed_date ? ` · Built ${d.build_completed_date.slice(0, 10)}` : ""}
                </div>
              </div>
            </div>

            {sqMissing && (
              <div className="bg-amber-50 text-amber-700 text-xs px-3 py-2 rounded mb-3">
                ⚠ PM Board square count missing — Robert needs to enter post-build numbers before crew payment can be approved.
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <Field label="Crew vendor">
                <select
                  value={e.vendorId}
                  onChange={(ev) => setEdits((prev) => ({ ...prev, [d.id]: { ...prev[d.id], vendorId: ev.target.value } }))}
                  className="text-sm border border-slate-300 rounded px-2 py-1 w-full"
                >
                  <option value="">— select —</option>
                  {vendors.map((v) => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </select>
              </Field>
              <Field label="Total squares">
                <div className="font-semibold text-bardin-ink">
                  {sq} sq{" "}
                  {isAutoBump && <span className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">⚠ ≥60sq → auto-bump $100</span>}
                  {!isAutoBump && sq > 0 && <span className="ml-1 text-[10px] text-slate-500">locked · from PM Board</span>}
                </div>
              </Field>
              <Field label="Rate per sq">
                <div className="font-semibold">${Number(rate).toFixed(2)}</div>
              </Field>
              <Field label="Flat fee">
                <input
                  type="number"
                  step="1"
                  value={e.flatFee || ""}
                  onChange={(ev) => setEdits((prev) => ({ ...prev, [d.id]: { ...prev[d.id], flatFee: parseFloat(ev.target.value || "0") } }))}
                  className="w-28 text-sm border border-slate-300 rounded px-2 py-1 text-right"
                />
              </Field>
              <Field label="Additional approved work ($)">
                <input
                  type="number"
                  step="0.01"
                  value={e.addtlAmount || ""}
                  placeholder="0.00"
                  onChange={(ev) => setEdits((prev) => ({ ...prev, [d.id]: { ...prev[d.id], addtlAmount: parseFloat(ev.target.value || "0") } }))}
                  className="w-32 text-sm border border-slate-300 rounded px-2 py-1 text-right"
                />
              </Field>
              <Field label="Additional work description">
                <input
                  type="text"
                  value={e.addtlDesc}
                  placeholder={e.addtlAmount > 0 ? "Required when amount > 0" : "Optional"}
                  onChange={(ev) => setEdits((prev) => ({ ...prev, [d.id]: { ...prev[d.id], addtlDesc: ev.target.value } }))}
                  className="w-full text-sm border border-slate-300 rounded px-2 py-1"
                />
              </Field>
            </div>

            <div className="mt-3 px-3 py-2 rounded-lg bg-bardin-orange/10 border border-bardin-orange/30 flex items-center justify-between">
              <span className="text-sm text-slate-700">
                Total — {sq} × ${Number(rate).toFixed(2)} + ${Number(e.flatFee).toFixed(2)}{e.addtlAmount ? ` + $${Number(e.addtlAmount).toFixed(2)}` : ""}
              </span>
              <span className="text-lg font-extrabold text-bardin-orange">{fmtUSD(total)}</span>
            </div>

            {canApprove && (
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => approveCrew(d)}
                  disabled={isActing || sqMissing || !e.vendorId}
                  className="px-4 py-1.5 text-xs font-medium rounded bg-bardin-orange text-white hover:opacity-90 disabled:opacity-50"
                >
                  {isActing ? "Approving…" : "Approve crew payment"}
                </button>
                {e.addtlDesc && e.addtlAmount > 0 && (
                  <span className="text-[11px] text-slate-500 self-center italic">
                    "{e.addtlDesc}" will appear in Risol's payment email
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3">
      <label className="text-xs text-slate-500 w-32 flex-shrink-0">{label}</label>
      <div className="flex-1">{children}</div>
    </div>
  );
}
